const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://api.comnieuthienly.vn/api";
const API_V2_BASE_URL = import.meta.env.VITE_API_V2_BASE_URL || "https://apiv2.comnieuthienly.vn/api";

export interface CompanyItem {
  id: number;
  name: string;
  token?: string;
  [key: string]: unknown;
}

export interface LoginResponse {
  token: string;
  user: Record<string, unknown>;
}

export interface GetAppDataResponse {
  current_user?: Record<string, unknown>;
  role_title?: string;
  [key: string]: unknown;
}

interface ApiResponse<T> {
  msg_code?: string;
  msg?: string;
  message?: string;
  data?: T;
}

interface ProfileApiResponse {
  success?: boolean;
  msg_code?: string;
  msg?: string;
  message?: string;
  data?: Record<string, unknown>;
}

interface RequestOptions {
  body?: unknown;
  companyId?: number;
  method?: "GET" | "POST";
  token?: string;
}

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== "object") {
    return fallback;
  }

  const candidateKeys = ["message", "msg"];
  for (const key of candidateKeys) {
    const value = (payload as Record<string, unknown>)[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }

  const nestedData = (payload as Record<string, unknown>).data;
  if (nestedData && typeof nestedData === "object") {
    for (const key of candidateKeys) {
      const value = (nestedData as Record<string, unknown>)[key];
      if (typeof value === "string" && value.trim()) {
        return value;
      }
    }
  }

  return fallback;
}

async function requestJson<T>(
  baseUrl: string,
  path: string,
  { body, companyId, method = "GET", token }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.token = token;
  }

  if (companyId != null) {
    headers["company-id"] = String(companyId);
  }

  const response = await fetch(`${baseUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const rawText = await response.text();
  let payload: unknown = null;

  if (rawText) {
    try {
      payload = JSON.parse(rawText) as unknown;
    } catch {
      payload = rawText;
    }
  }

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, "Không thể kết nối đến hệ thống"));
  }

  return payload as T;
}

function getNameFromField(field: unknown): string | null {
  if (typeof field === "string") {
    return field;
  }

  if (field && typeof field === "object" && "name" in field) {
    const name = (field as { name?: string }).name;
    return typeof name === "string" ? name : null;
  }

  return null;
}

export async function getListCompany(payload: {
  phone_number: string;
  password: string;
}): Promise<CompanyItem[]> {
  const res = await requestJson<ApiResponse<CompanyItem[]>>(API_BASE_URL, "/multi_company_exists", {
    method: "POST",
    body: payload,
  });

  if (res.msg_code !== "SUCCESS") {
    throw new Error(getErrorMessage(res, "Không tìm thấy thông tin công ty cho tài khoản này"));
  }

  return res.data || [];
}

export async function accountLogin(payload: {
  company_id: number;
  phone_number: string;
  password: string;
}): Promise<LoginResponse> {
  const res = await requestJson<ApiResponse<LoginResponse>>(API_BASE_URL, "/login", {
    method: "POST",
    body: payload,
    companyId: payload.company_id,
  });

  if (res.msg_code !== "SUCCESS" || !res.data?.token) {
    throw new Error(getErrorMessage(res, "Đăng nhập thất bại, vui lòng kiểm tra lại thông tin"));
  }

  return res.data;
}

export async function getAppData(token: string, companyId: number): Promise<GetAppDataResponse> {
  const res = await requestJson<ProfileApiResponse>(API_V2_BASE_URL, "/profile?is_full=true", {
    method: "GET",
    token,
    companyId,
  });

  const ok = res.success === true || res.msg_code === "SUCCESS";
  if (!ok || !res.data) {
    throw new Error(getErrorMessage(res, "Không thể xác minh thông tin tài khoản"));
  }

  const profile = res.data as Record<string, unknown>;
  const roleTitle =
    getNameFromField(profile.position) ??
    (typeof profile.position_name === "string" ? profile.position_name : null);

  return {
    current_user: profile,
    role_title: roleTitle ?? undefined,
  };
}
